import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Plus, ExternalLink, Trash2, Eye, EyeOff, FileText, X } from "lucide-react";

export default function WebsitePages() {
  const { activeTenant } = useTenant();
  const [pages, setPages] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ slug: "", title: "", page_type: "custom" });

  const load = async () => {
    if (!activeTenant) return;
    const { data } = await api.get("/pages");
    setPages(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?.id]);

  const create = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/pages", { ...draft, status: "draft", nav_visible: true, blocks: [], seo: {} });
    setCreating(false);
    setDraft({ slug: "", title: "", page_type: "custom" });
    window.location.href = `/admin/website/pages/${data.id}`;
  };

  const del = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/pages/${id}`);
    load();
  };

  const togglePublish = async (p, e) => {
    e?.stopPropagation?.();
    const next = p.status === "published" ? "draft" : "published";
    const verb = next === "draft" ? "Unpublish" : "Publish";
    if (!window.confirm(`${verb} "${p.title}"?`)) return;
    await api.patch(`/pages/${p.id}`, { status: next });
    load();
  };

  if (!activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Select a tenant.</div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="website-pages-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Website · {activeTenant.name}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Pages</h1>
        </div>
        <div className="flex gap-2">
          <a href={`/site/${activeTenant.slug}`} target="_blank" rel="noreferrer" className="n-btn-ghost n-btn flex items-center gap-2" data-testid="preview-site-button">
            <ExternalLink size={14} /> PREVIEW SITE
          </a>
          <button onClick={() => setCreating(true)} className="n-btn flex items-center gap-2" data-testid="new-page-button">
            <Plus size={14} /> New Page
          </button>
        </div>
      </div>

      <div className="n-card overflow-hidden" data-testid="pages-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] border-b border-[var(--n-border)]">
            <tr className="text-left">
              {["Title", "Path", "Type", "Status", "Blocks", "In nav", "Updated", ""].map((h) => (
                <th key={h} className="n-label py-2 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className="border-b border-[var(--n-border)] hover:bg-[#101015]" data-testid={`page-row-${p.slug.replace(/[^a-z0-9]/gi, "-")}`}>
                <td className="px-4 py-3 font-cab font-bold">
                  <Link to={`/admin/website/pages/${p.id}`} className="hover:underline flex items-center gap-2">
                    <FileText size={12} /> {p.title}
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono-plex text-xs text-[var(--n-text-sec)]">{p.slug}</td>
                <td className="px-4 py-3"><span className="n-chip">{p.page_type}</span></td>
                <td className="px-4 py-3">
                  <button type="button" onClick={(e) => togglePublish(p, e)} className="n-chip flex items-center gap-1" data-testid={`page-status-${p.slug.replace(/[^a-z0-9]/gi, "-")}`}>
                    {p.status === "published" ? <><Eye size={10} className="text-[var(--n-success)]"/>published</> : <><EyeOff size={10} className="text-[var(--n-warning)]"/>draft</>}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono-plex text-xs text-[var(--n-text-sec)]">—</td>
                <td className="px-4 py-3 text-xs text-[var(--n-text-sec)]">{p.nav_visible ? "yes" : "—"}</td>
                <td className="px-4 py-3 font-mono-plex text-[10px] text-[var(--n-text-muted)]">{p.updated_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => del(p.id)} className="text-[var(--n-danger)]" data-testid={`page-delete-${p.slug.replace(/[^a-z0-9]/gi, "-")}`}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {pages.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[var(--n-text-muted)]">No pages yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="n-card w-full max-w-md p-6" data-testid="new-page-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-cab font-black text-xl">New page</div>
              <button type="button" onClick={() => setCreating(false)}><X size={18} /></button>
            </div>
            <div className="mb-3">
              <label className="n-label block mb-1">Title</label>
              <input className="n-input" required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="new-page-title" />
            </div>
            <div className="mb-3">
              <label className="n-label block mb-1">Path (must start with /)</label>
              <input className="n-input font-mono-plex" required value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="/about" data-testid="new-page-slug" />
            </div>
            <div className="mb-4">
              <label className="n-label block mb-1">Type</label>
              <select className="n-input" value={draft.page_type} onChange={(e) => setDraft({ ...draft, page_type: e.target.value })}>
                {["custom", "landing", "blog", "service_detail", "city", "legal"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button type="submit" className="n-btn w-full" data-testid="new-page-submit">CREATE</button>
          </form>
        </div>
      )}
    </div>
  );
}
